import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { MobileShell } from "@/components/MobileShell";
import { RecipeDrawer } from "@/components/RecipeDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, getApiError } from "@/lib/api";
import { buildRecipeShareText, getShareSuccessMessage, shareContent } from "@/lib/share";
import { cn } from "@/lib/utils";


const filters = [
  { label: "Alles", value: "all" },
  { label: "Ontbijt", value: "breakfast" },
  { label: "Lunch", value: "lunch" },
  { label: "Diner", value: "dinner" },
  { label: "Snack", value: "snack" },
];


export default function RecipesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const [mealType, setMealType] = useState("all");
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    const fetchRecipes = async () => {
      const response = await api.get("/recipes", {
        params: {
          search: search || undefined,
          meal_type: mealType === "all" ? undefined : mealType,
        },
      });
      setRecipes(response.data);
    };

    fetchRecipes();
  }, [mealType, search]);

  const openRecipe = async (recipeId) => {
    try {
      const response = await api.get(`/recipes/${recipeId}`);
      setSelectedRecipe(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  useEffect(() => {
    const recipeParam = searchParams.get("recipe");
    if (!recipeParam) {
      return;
    }
    openRecipe(recipeParam);
  }, [searchParams]);

  const featuredRecipe = recipes[0];

  const shareRecipe = async () => {
    if (!selectedRecipe) {
      return;
    }

    try {
      const mode = await shareContent({
        title: selectedRecipe.title,
        text: buildRecipeShareText(selectedRecipe),
        url: `${window.location.origin}/recipes?recipe=${selectedRecipe.slug || selectedRecipe.id}`,
      });
      toast.success(getShareSuccessMessage(mode, "Recept"));
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error("Recept delen lukte niet.");
    }
  };

  return (
    <>
      <MobileShell title="Recepten" eyebrow="Visueel rijk, planner-simpel" action={<Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => navigate("/planner")} data-testid="recipes-open-planner-button">Planner</Button>}>
        <div className="space-y-4">
          <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="recipes-search-card">
            <div className="flex items-center gap-3 rounded-[22px] border border-stone-200 bg-stone-50 px-3" data-testid="recipes-search-wrapper">
              <Search className="h-4 w-4 text-stone-500" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Zoek op receptnaam" className="h-12 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" data-testid="recipes-search-input" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="recipes-filter-group">
              {filters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setMealType(filter.value)}
                  className={cn(
                    "rounded-full px-4 py-2 text-sm transition-all duration-200",
                    mealType === filter.value ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-600",
                  )}
                  data-testid={`recipes-filter-${filter.value}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </section>

          {featuredRecipe ? (
            <section className="overflow-hidden rounded-[30px] border border-stone-200 bg-white/90" data-testid="recipes-featured-card">
              <img src={featuredRecipe.image_url} alt={featuredRecipe.title} className="aspect-[4/3] w-full object-cover" data-testid="recipes-featured-image" />
              <div className="p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="recipes-featured-kicker">
                  Uitgelicht
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="recipes-featured-title">
                  {featuredRecipe.title}
                </h2>
                <p className="mt-2 text-sm text-stone-600" data-testid="recipes-featured-description">
                  {featuredRecipe.description}
                </p>
                <Button className="mt-4 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={() => openRecipe(featuredRecipe.id)} data-testid="recipes-featured-open-button">
                  Bekijk recept
                </Button>
              </div>
            </section>
          ) : null}

          <section className="grid grid-cols-1 gap-3" data-testid="recipes-grid">
            {recipes.map((recipe, index) => (
              <button
                key={recipe.id}
                type="button"
                onClick={() => openRecipe(recipe.id)}
                className="stagger-enter overflow-hidden rounded-[26px] border border-stone-200 bg-white/90 text-left meal-row-hover"
                style={{ animationDelay: `${index * 45 + 60}ms` }}
                data-testid={`recipes-card-${recipe.id}`}
              >
                <div className="flex items-center gap-4 p-3">
                  <img src={recipe.thumbnail_url} alt={recipe.title} className="h-20 w-20 rounded-[20px] object-cover" data-testid={`recipes-card-image-${recipe.id}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700" data-testid={`recipes-card-type-${recipe.id}`}>
                      {recipe.meal_type}
                    </p>
                    <h3 className="mt-1 line-clamp-2 text-base font-semibold text-stone-900" data-testid={`recipes-card-title-${recipe.id}`}>
                      {recipe.title}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-stone-500">
                      <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700" data-testid={`recipes-card-kcal-${recipe.id}`}>
                        {recipe.kcal_per_serving} kcal
                      </span>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid={`recipes-card-carbs-${recipe.id}`}>
                        {recipe.carbs_per_serving} g carbs
                      </span>
                      <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid={`recipes-card-time-${recipe.id}`}>
                        {recipe.prep_time_min + recipe.cook_time_min} min
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </section>
        </div>
      </MobileShell>

      <RecipeDrawer
        open={Boolean(selectedRecipe)}
        onOpenChange={(open) => !open && setSelectedRecipe(null)}
        recipe={selectedRecipe}
        onShareRecipe={shareRecipe}
        onGoToGrocery={() => {
          setSelectedRecipe(null);
          navigate("/grocery");
        }}
      />
    </>
  );
}